/**
 * A yes/no answer as a form field: the sunken pill every dialog uses, with a
 * tick box in it.
 *
 * The box itself is the shared Radix one from `@wa/ui` rather than the browser's
 * own. `.crm-inp` deliberately leaves `input[type="checkbox"]` alone and lets the
 * platform paint it, which is honest but is also the one control in a dialog
 * that still looks like the operating system: a 17px square with the OS corner
 * radius and the OS tick, standing in a row of 52px pills that were drawn by
 * this app. The permissions card and the call queue had already moved to the
 * shared box, so the two looks were live side by side.
 *
 * A component rather than five copies of the same three lines: the pill, the
 * gap and the 13.5px label are the shape, and five hand-written copies is how
 * one of them ends up 13px with no gap.
 *
 * The whole pill is the target, not just the box - a 16px square inside a 52px
 * row is a small thing to hit, and the words beside it are what the reader is
 * aiming at anyway.
 */
import type { ReactNode } from "react";
import { Checkbox, cn } from "@wa/ui";

export function CheckField({
  checked,
  onChange,
  label,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** The words beside the box - they are the control's accessible name. */
  label: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <label
      className={cn(
        "crm-inp",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        className,
      )}
    >
      <Checkbox
        checked={checked}
        // Radix hands back `boolean | "indeterminate"`, and this field has two
        // states only. Anything that is not a definite yes is a no.
        onCheckedChange={(next) => onChange(next === true)}
        disabled={disabled}
      />
      <span className="min-w-0 flex-1 text-[13.5px]">{label}</span>
    </label>
  );
}
